import "./index.css";
import {
	AlertTriangle,
	EyeOff,
	GripVertical,
	Link2,
	Lock,
	Music,
	Plus,
	Sparkles,
	Unlock,
} from "lucide-react";
import { useCallback, useState } from "react";
import { ArpeggiatorControls } from "./components/ArpeggiatorControls.tsx";
import { ChorusControls } from "./components/ChorusControls.tsx";
import { DelayControls } from "./components/DelayControls.tsx";
import { Keyboard } from "./components/Keyboard.tsx";
import { LimiterControls } from "./components/LimiterControls.tsx";
import { MasterVolume } from "./components/MasterVolume.tsx";
import { MidiInputPanel } from "./components/MidiInputPanel.tsx";
import { MidiPlayer } from "./components/MidiPlayer.tsx";
import { Mixer } from "./components/Mixer.tsx";
import { OscillatorLayers } from "./components/OscillatorLayers.tsx";
import { OutputDeviceSelector } from "./components/OutputDeviceSelector.tsx";
import { PresetSelector } from "./components/PresetSelector.tsx";
import { Sequencer } from "./components/Sequencer.tsx";
import { SpectralScope } from "./components/SpectralScope.tsx";
import { TempoControl } from "./components/TempoControl.tsx";
import { TuningControls } from "./components/TuningControls.tsx";
import { WaveformScope } from "./components/WaveformScope.tsx";
import { useKeyboardInput } from "./hooks/useKeyboardInput.ts";
import { useMidiInput } from "./hooks/useMidiInput.ts";
import type { SectionId } from "./hooks/useSectionOrder.ts";
import { useSectionOrder } from "./hooks/useSectionOrder.ts";
import { SynthProvider, useSynth } from "./hooks/useSynth.tsx";

/** The first note on the keyboard (C3 = MIDI 48). */
const START_NOTE = 48;
/** The last note on the keyboard (F4 = MIDI 65). */
const END_NOTE = 65;

/** Human-readable labels for each section. */
const SECTION_LABELS: Record<SectionId, string> = {
	"midi-input": "MIDI Input",
	mixer: "Mixer",
	"synth-settings": "Synth Settings",
	scope: "Scope",
	"midi-player": "MIDI Player",
	sequencer: "Sequencer",
} as const;

function SynthUI() {
	const {
		audioContext,
		isReady,
		isLoading,
		error,
		params,
		activeNotes,
		tuning,
		adsrLinked,
		filterLinked,
		init,
		noteOn,
		noteOff,
		setADSR,
		setFilter,
		setMasterBusGain,
		masterBusGain,
		masterAnalyser,
		setTempo,
		setTuning,
		setOscillatorLayers,
		updateLayer,
		updateLayerADSR,
		updateLayerFilter,
		setAdsrLinked,
		setFilterLinked,
		setDelay,
		delayParams,
		setLimiter,
		limiterParams,
		arpParams,
		setArp,
		chorusParams,
		setChorus,
		allNotesOff,
		panic,
		synthInstances,
		activeSynthId,
		addSynth,
		removeSynth,
		selectSynth,
		renameSynth,
		setSynthMidiChannel,
		setSynthVolume,
		setSynthPan,
		setSynthMuted,
		setSynthSolo,
		midiNoteOn,
		midiNoteOff,
		synthNoteOn,
		synthNoteOff,
		getShareURL,
		loadPreset,
		initialSequencerNotes,
		initialSequencerMeasures,
		saveSequencerState,
		randomizeParams,
		exportPreset,
		importPreset,
	} = useSynth();

	useKeyboardInput({
		noteOn,
		noteOff,
		enabled: isReady,
		tuning,
		setTuning,
	});

	const midi = useMidiInput({
		noteOn: midiNoteOn,
		noteOff: midiNoteOff,
		setFilter,
		setADSR,
		setMasterGain: setMasterBusGain,
		enabled: isReady,
	});

	const [activePanel, setActivePanel] = useState<
		"synth" | "delay" | "limiter" | "arp" | "chorus"
	>("synth");
	const [shareTooltip, setShareTooltip] = useState<string | null>(null);
	const sectionOrder = useSectionOrder();

	const handleShare = useCallback(async () => {
		try {
			const url = getShareURL();
			await navigator.clipboard.writeText(url);
			setShareTooltip("Link copied!");
			setTimeout(() => setShareTooltip(null), 2000);
		} catch {
			setShareTooltip("Copy failed");
			setTimeout(() => setShareTooltip(null), 2000);
		}
	}, [getShareURL]);

	if (!isReady) {
		return (
			<div className="app">
				<div className="init-screen">
					<Music size={64} className="init-icon" />
					<h1>WASM Synth</h1>
					<p>Browser synthesizer powered by Rust &amp; WebAssembly</p>
					{error ? (
						<>
							<p className="error-message">{error}</p>
							<button type="button" className="init-button" onClick={init}>
								Retry
							</button>
						</>
					) : (
						<p className="init-hint">
							{isLoading
								? "Loading WASM\u2026 click anywhere if audio doesn\u2019t start"
								: "Initializing\u2026"}
						</p>
					)}
				</div>
			</div>
		);
	}

	const totalSemitones = tuning.octaveOffset * 12 + tuning.semitoneOffset;

	/** Render a section by its ID. */
	const renderSection = (id: SectionId) => {
		switch (id) {
			case "midi-input":
				return <MidiInputPanel midi={midi} />;

			case "mixer":
				return (
					<Mixer
						instances={synthInstances}
						activeId={activeSynthId}
						onSelect={selectSynth}
						onAdd={addSynth}
						onRemove={removeSynth}
						onRename={renameSynth}
						onMidiChannel={setSynthMidiChannel}
						onVolumeChange={setSynthVolume}
						onPanChange={setSynthPan}
						onMuteToggle={setSynthMuted}
						onSoloToggle={setSynthSolo}
					/>
				);

			case "synth-settings":
				return (
					<div
						className={`control-panel ${activePanel === "delay" ? "delay-view" : activePanel === "limiter" ? "limiter-view" : activePanel === "arp" ? "arp-view" : activePanel === "chorus" ? "chorus-view" : "synth-view"}`}
					>
						<div className="panel-tabs">
							<button
								type="button"
								className={`panel-tab-btn ${activePanel === "synth" ? "active" : ""}`}
								onClick={() => setActivePanel("synth")}
							>
								Synth
							</button>
							<button
								type="button"
								className={`panel-tab-btn ${activePanel === "delay" ? "active" : ""}`}
								onClick={() => setActivePanel("delay")}
							>
								Delay
							</button>
							<button
								type="button"
								className={`panel-tab-btn ${activePanel === "limiter" ? "active" : ""}`}
								onClick={() => setActivePanel("limiter")}
							>
								Limiter
							</button>
							<button
								type="button"
								className={`panel-tab-btn ${activePanel === "arp" ? "active" : ""}`}
								onClick={() => setActivePanel("arp")}
							>
								Arp
							</button>
							<button
								type="button"
								className={`panel-tab-btn ${activePanel === "chorus" ? "active" : ""}`}
								onClick={() => setActivePanel("chorus")}
							>
								Chorus
							</button>
							<PresetSelector
								onLoadPreset={loadPreset}
								onRandomize={randomizeParams}
								onExportPreset={exportPreset}
								onImportPreset={importPreset}
							/>
						</div>
						{activePanel === "synth" ? (
							<OscillatorLayers
								layers={params.oscillatorLayers}
								adsrLinked={adsrLinked}
								filterLinked={filterLinked}
								onLayersChange={setOscillatorLayers}
								onLayerUpdate={updateLayer}
								onLayerADSR={updateLayerADSR}
								onLayerFilter={updateLayerFilter}
								onAdsrLinkedChange={setAdsrLinked}
								onFilterLinkedChange={setFilterLinked}
							/>
						) : activePanel === "delay" ? (
							<div className="delay-panel">
								<DelayControls
									params={delayParams}
									tempo={params.tempo}
									onChange={setDelay}
								/>
							</div>
						) : activePanel === "limiter" ? (
							<div className="limiter-panel">
								<LimiterControls
									params={limiterParams}
									onChange={setLimiter}
									enabled={isReady}
								/>
							</div>
						) : activePanel === "arp" ? (
							<div className="arp-panel">
								<ArpeggiatorControls params={arpParams} onChange={setArp} />
							</div>
						) : (
							<div className="chorus-panel">
								<ChorusControls params={chorusParams} onChange={setChorus} />
							</div>
						)}
					</div>
				);

			case "scope":
				return (
					<>
						<WaveformScope analyser={masterAnalyser} />
						<SpectralScope analyser={masterAnalyser} />
					</>
				);

			case "midi-player":
				return (
					<MidiPlayer
						noteOn={noteOn}
						noteOff={noteOff}
						enabled={isReady}
						allNotesOff={allNotesOff}
					/>
				);

			case "sequencer":
				return (
					<Sequencer
						synthNoteOn={synthNoteOn}
						synthNoteOff={synthNoteOff}
						synthInstances={synthInstances}
						tempo={params.tempo}
						enabled={isReady}
						activeSynthId={activeSynthId}
						initialNotes={initialSequencerNotes}
						initialMeasures={initialSequencerMeasures}
						onStateChange={saveSequencerState}
					/>
				);

			default:
				return null;
		}
	};

	return (
		<div className="app">
			<header className="header">
				<div className="header-left">
					<Music size={20} />
					<h1>Crustacean Cerulean</h1>
				</div>
				<div className="header-right">
					<span className="status-indicator active" />
					<span className="voice-count">Voices: {activeNotes.size}</span>
					{tuning.octaveOffset !== 0 && (
						<span className="octave-indicator">
							Oct:{" "}
							{tuning.octaveOffset > 0
								? `+${tuning.octaveOffset}`
								: tuning.octaveOffset}
						</span>
					)}
					{tuning.semitoneOffset !== 0 && (
						<span className="octave-indicator">
							Semi:{" "}
							{tuning.semitoneOffset > 0
								? `+${tuning.semitoneOffset}`
								: tuning.semitoneOffset}
						</span>
					)}
					{tuning.centOffset !== 0 && (
						<span className="octave-indicator">
							Cent:{" "}
							{tuning.centOffset > 0
								? `+${tuning.centOffset}`
								: tuning.centOffset}
						</span>
					)}
					{midi.sustainPedalDown && (
						<span className="octave-indicator">SUS</span>
					)}

					{/* Section reorder lock toggle */}
					<button
						type="button"
						className={`section-lock-btn ${sectionOrder.locked ? "locked" : ""}`}
						onClick={() => sectionOrder.setLocked(!sectionOrder.locked)}
						title={
							sectionOrder.locked
								? "Unlock sections (allow reorder)"
								: "Lock sections (prevent reorder)"
						}
					>
						{sectionOrder.locked ? <Lock size={12} /> : <Unlock size={12} />}
					</button>

					{/* Features preview link */}
					<a
						href="./features/"
						className="share-btn"
						title="Browse feature previews"
					>
						<Sparkles size={14} />
						<span>Features</span>
					</a>

					{/* Share button */}
					<button
						type="button"
						className="share-btn"
						onClick={handleShare}
						title="Copy shareable link to clipboard"
					>
						<Link2 size={14} />
						<span>{shareTooltip ?? "Share"}</span>
					</button>

					<button
						type="button"
						className="panic-btn"
						onClick={panic}
						title="Panic — stop all sound immediately"
					>
						<AlertTriangle size={14} />
						<span>Panic</span>
					</button>
				</div>
			</header>

			{/* Global tempo & tuning — single source of truth for the whole app */}
			<div className="global-bar">
				<TempoControl value={params.tempo} onChange={setTempo} />
				<TuningControls value={tuning} onChange={setTuning} />
				<MasterVolume value={masterBusGain} onChange={setMasterBusGain} />
				<OutputDeviceSelector audioContext={audioContext} />
			</div>

			{/* Reorderable sections */}
			{sectionOrder.order
				.filter((id) => !sectionOrder.hidden.has(id))
				.map((sectionId, index) => {
					const handlers = sectionOrder.dragHandlers(index);
					return (
						// biome-ignore lint/a11y/noStaticElementInteractions: intentional drag-and-drop wrapper
						<div
							key={sectionId}
							className={`reorderable-section ${sectionOrder.dragOverIndex === index ? "drag-over" : ""}`}
							draggable={handlers.draggable}
							onDragStart={handlers.onDragStart}
							onDragOver={handlers.onDragOver}
							onDrop={handlers.onDrop}
							onDragEnd={handlers.onDragEnd}
						>
							{!sectionOrder.locked && (
								<div className="section-drag-handle" title="Drag to reorder">
									<GripVertical size={12} />
								</div>
							)}
							{!sectionOrder.locked && (
								<button
									type="button"
									className="section-hide-btn"
									onClick={() => sectionOrder.hideSection(sectionId)}
									title={`Hide ${SECTION_LABELS[sectionId]}`}
								>
									<EyeOff size={12} />
								</button>
							)}
							{renderSection(sectionId)}
						</div>
					);
				})}

			{/* Restore hidden sections panel */}
			{sectionOrder.hidden.size > 0 && (
				<div className="hidden-sections-restore">
					<span className="hidden-sections-label">Hidden:</span>
					{sectionOrder.order
						.filter((id) => sectionOrder.hidden.has(id))
						.map((id) => (
							<button
								key={id}
								type="button"
								className="restore-section-btn"
								onClick={() => sectionOrder.showSection(id)}
								title={`Show ${SECTION_LABELS[id]}`}
							>
								<Plus size={10} />
								<span>{SECTION_LABELS[id]}</span>
							</button>
						))}
				</div>
			)}

			<Keyboard
				startNote={START_NOTE + totalSemitones}
				endNote={END_NOTE + totalSemitones}
				activeNotes={activeNotes}
				octaveOffset={tuning.octaveOffset}
				onNoteOn={noteOn}
				onNoteOff={noteOff}
			/>

			<footer className="footer">
				<span>Keys: A–' (white) W,E,T,Y,U,O,P (black) | Z/X: octave shift</span>
			</footer>
		</div>
	);
}

export function App() {
	return (
		<SynthProvider>
			<SynthUI />
		</SynthProvider>
	);
}

export default App;
